/**
 * Parser for mermaid's `xychart-beta` grammar, so chat can render those
 * fences with an interactive chart instead of mermaid's static SVG.
 *
 * Accepts everything mermaid does (title, optional `horizontal`, category or
 * numeric x-axis, labelled y-axis with a range, `bar` / `line` series with an
 * optional quoted title) plus `area` series, which models write often enough
 * that rejecting them just shows the reader raw source.
 */

import { paletteFromFrontMatter, splitMermaidSource, type FigurePalette } from "./mermaidSource";

export type XySeriesKind = "bar" | "line" | "area";

export interface XySeries {
  readonly kind: XySeriesKind;
  readonly title: string | null;
  readonly values: ReadonlyArray<number>;
}

export interface XyAxisRange {
  readonly min: number;
  readonly max: number;
}

export interface XyChartSpec {
  readonly title: string | null;
  readonly horizontal: boolean;
  readonly xLabel: string | null;
  /** Category labels, or evenly spaced numbers when the axis was a range. */
  readonly categories: ReadonlyArray<string>;
  readonly yLabel: string | null;
  readonly yRange: XyAxisRange | null;
  readonly series: ReadonlyArray<XySeries>;
  /** Series colours from mermaid's `xyChart.plotColorPalette`, when set. */
  readonly palette: FigurePalette | null;
  /** mermaid's `xyChart.showDataLabel` / `showDataLabelOutsideBar`: print values on bars. */
  readonly showDataLabel: boolean;
  /**
   * `xyChart.stacked: true` in the front matter — our extension, mermaid has
   * no stacked bars — piles bar (and area) series on top of each other.
   */
  readonly stacked: boolean;
}

const HEADER_REGEX = /^xychart(?:-beta)?(?:\s+(horizontal|vertical))?\s*$/i;
const NUMBER_REGEX = /^[+-]?(?:\d+\.?\d*|\.\d+)$/;

export function isXyChartSource(code: string): boolean {
  const firstLine = splitMermaidSource(code).lines[0];
  return firstLine != null && HEADER_REGEX.test(firstLine);
}

/**
 * Splits a `[a, "b c", 3]` list into trimmed items. Quotes are kept when
 * `keepQuotes` is set (series values may carry a quoted point label) and
 * stripped otherwise (category labels).
 */
function parseList(text: string, keepQuotes = false): ReadonlyArray<string> | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith("[") || !trimmed.endsWith("]")) return null;
  const body = trimmed.slice(1, -1);
  const items: string[] = [];
  let current = "";
  let quote: string | null = null;
  for (const char of body) {
    if (quote) {
      if (char === quote) {
        if (keepQuotes) current += char;
        quote = null;
      } else {
        current += char;
      }
    } else if (char === '"' || char === "'") {
      quote = char;
      if (keepQuotes) current += char;
    } else if (char === ",") {
      items.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  if (quote) return null;
  if (current.trim().length > 0 || items.length > 0) items.push(current.trim());
  return items;
}

/** Mermaid lets a point carry a label: `line [2.3 "Peak", 1]`. */
const LABELLED_NUMBER_REGEX = /^([+-]?(?:\d+\.?\d*|\.\d+))\s+(?:"[^"]*"|'[^']*')$/;

function parseNumbers(items: ReadonlyArray<string>): ReadonlyArray<number> | null {
  const values: number[] = [];
  for (const item of items) {
    const labelled = item.match(LABELLED_NUMBER_REGEX)?.[1];
    if (labelled == null && !NUMBER_REGEX.test(item)) return null;
    values.push(Number(labelled ?? item));
  }
  return values;
}

/**
 * `"label" rest` → { label, rest }. Mermaid also allows a bare one-word
 * label (`line Revenue [2, 3]`, `x-axis Quarter [Q1, Q2]`), so an unquoted
 * word before a `[list]` or a `min --> max` range is a label too.
 */
function splitLeadingQuoted(text: string): { label: string | null; rest: string } {
  const trimmed = text.trim();
  const quote = trimmed[0];
  if (quote !== '"' && quote !== "'") {
    const bare = trimmed.match(/^([^\s"'\[]+)\s+(\[.*\]|[+-]?[\d.]+\s*-->\s*[+-]?[\d.]+)$/);
    if (bare?.[1] && bare[2]) return { label: bare[1], rest: bare[2] };
    return { label: null, rest: trimmed };
  }
  const end = trimmed.indexOf(quote, 1);
  if (end === -1) return { label: null, rest: trimmed };
  return { label: trimmed.slice(1, end), rest: trimmed.slice(end + 1).trim() };
}

function parseRange(text: string): XyAxisRange | null {
  const match = text.trim().match(/^([+-]?[\d.]+)\s*-->\s*([+-]?[\d.]+)$/);
  if (!match?.[1] || !match[2]) return null;
  const min = Number(match[1]);
  const max = Number(match[2]);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) return null;
  return { min, max };
}

function rangeCategories(range: XyAxisRange, count: number): ReadonlyArray<string> {
  if (count <= 1) return [String(range.min)];
  const step = (range.max - range.min) / (count - 1);
  // Trim binary noise (0.30000000000000004) without collapsing tiny ranges.
  return Array.from({ length: count }, (_, index) =>
    String(Number((range.min + step * index).toPrecision(12))),
  );
}

/**
 * A `key: true` line inside an `xyChart:` block of the front matter, read
 * the way YAML nests: the line must sit deeper than the `xyChart:` line and
 * before indentation returns to that level. A `# key: true` comment, a
 * quoted value, or the key at another path does not count.
 */
function xyChartFlag(frontMatter: string | null, key: string): boolean {
  if (frontMatter == null) return false;
  const flag = new RegExp(`^\\s*${key}\\s*:\\s*true\\s*(?:#.*)?$`);
  let blockIndent: number | null = null;
  for (const line of frontMatter.split("\n")) {
    if (line.trim().length === 0 || line.trim().startsWith("#")) continue;
    const indent = line.length - line.trimStart().length;
    if (blockIndent != null && indent <= blockIndent) blockIndent = null;
    if (blockIndent != null && flag.test(line)) return true;
    if (blockIndent == null && /^\s*xyChart\s*:\s*$/.test(line)) blockIndent = indent;
  }
  return false;
}

/**
 * Returns null when the text is not a complete, well-formed xy chart; callers
 * fall back to the source view, exactly as a mermaid parse failure would.
 */
export function parseXyChart(code: string): XyChartSpec | null {
  const { frontMatter, lines, unterminated } = splitMermaidSource(code);
  if (unterminated) return null;
  const header = lines[0];
  if (header == null) return null;
  const headerMatch = header.match(HEADER_REGEX);
  if (!headerMatch) return null;

  let title: string | null = null;
  let xLabel: string | null = null;
  let categories: ReadonlyArray<string> | null = null;
  let xRange: XyAxisRange | null = null;
  let yLabel: string | null = null;
  let yRange: XyAxisRange | null = null;
  const series: XySeries[] = [];

  for (const line of lines.slice(1)) {
    const keywordMatch = line.match(/^([A-Za-z-]+)\s*(.*)$/);
    if (!keywordMatch?.[1]) return null;
    const keyword = keywordMatch[1].toLowerCase();
    const rest = keywordMatch[2] ?? "";

    switch (keyword) {
      case "title": {
        const { label } = splitLeadingQuoted(rest);
        title = label ?? rest.trim();
        break;
      }
      case "x-axis": {
        const { label, rest: after } = splitLeadingQuoted(rest);
        xLabel = label;
        if (after.length === 0) break;
        const list = parseList(after);
        if (list) {
          categories = list;
          break;
        }
        const range = parseRange(after);
        if (!range) return null;
        xRange = range;
        break;
      }
      case "y-axis": {
        const { label, rest: after } = splitLeadingQuoted(rest);
        yLabel = label;
        if (after.length === 0) break;
        const range = parseRange(after);
        if (!range) return null;
        yRange = range;
        break;
      }
      case "bar":
      case "line":
      case "area": {
        const { label, rest: after } = splitLeadingQuoted(rest);
        const list = parseList(after, true);
        const values = list ? parseNumbers(list) : null;
        if (!values || values.length === 0) return null;
        series.push({ kind: keyword, title: label, values });
        break;
      }
      default:
        return null;
    }
  }

  if (series.length === 0) return null;
  const pointCount = Math.max(...series.map((entry) => entry.values.length));
  const numbered = Array.from({ length: pointCount }, (_, index) => String(index + 1));
  // A category list shorter than the longest series would silently drop
  // points; number the missing positions so every value stays on the chart.
  const resolvedCategories = categories
    ? [...categories, ...numbered.slice(categories.length)]
    : xRange
      ? rangeCategories(xRange, pointCount)
      : numbered;

  return {
    title,
    horizontal: headerMatch[1]?.toLowerCase() === "horizontal",
    xLabel,
    categories: resolvedCategories,
    yLabel,
    yRange,
    series,
    palette: paletteFromFrontMatter(frontMatter, "xy"),
    showDataLabel:
      xyChartFlag(frontMatter, "showDataLabel") || xyChartFlag(frontMatter, "showDataLabelOutsideBar"),
    stacked: xyChartFlag(frontMatter, "stacked"),
  };
}

export interface XyChartRow {
  readonly category: string;
  readonly [seriesKey: string]: string | number | null;
}

export function xySeriesKey(index: number): string {
  return `s${index}`;
}

/** Name shown in tooltips and the legend for a series. */
export function xySeriesName(entry: XySeries, index: number, total: number): string {
  if (entry.title) return entry.title;
  const kindLabel = entry.kind === "bar" ? "Bar" : entry.kind === "line" ? "Line" : "Area";
  return total === 1 ? kindLabel : `${kindLabel} ${index + 1}`;
}

/** Recharts wants one row per x position with a key per series. */
export function xyChartRows(spec: XyChartSpec): ReadonlyArray<XyChartRow> {
  return spec.categories.map((category, rowIndex) => {
    const row: Record<string, string | number | null> = { category };
    spec.series.forEach((entry, seriesIndex) => {
      row[xySeriesKey(seriesIndex)] = entry.values[rowIndex] ?? null;
    });
    return row as XyChartRow;
  });
}

/**
 * Y domain: the declared range when the author gave one, otherwise recharts'
 * automatic fit. Declared ranges that would clip a value are widened so a bar
 * never silently disappears.
 */
export function xyChartDomain(spec: XyChartSpec): [number | "auto", number | "auto"] {
  if (!spec.yRange) {
    // Bars encode value as length, so they must start at zero; a line or
    // area chart may zoom to its data like mermaid does.
    const hasBars = spec.series.some((entry) => entry.kind === "bar");
    const minValue = Math.min(0, ...spec.series.flatMap((entry) => entry.values));
    return hasBars ? [minValue, "auto"] : ["auto", "auto"];
  }
  const values = spec.series.flatMap((entry) => entry.values);
  const min = Math.min(spec.yRange.min, ...values);
  const max = Math.max(spec.yRange.max, ...values);
  return [min, max];
}
